import { Component, OnInit } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { Table } from '../../shared/table/table';
import { TableAction, TableColumn } from '../../shared/models/table-models';
import { Popup } from '../../../core/shared/components/popup/popup';
import { Router, RouterModule } from '@angular/router';
import { faEye, faPencil, faTrash } from '@fortawesome/free-solid-svg-icons';
import { DynamicFieldConfig } from '../../../core/shared/dynamic-form/form-config.model';
import { Setup } from '../shared/service/setup';
import { ToastService } from '../../../core/shared/services/toast.service';
import { CommonCodeGroup } from '../shared/models/common-code.model';

@Component({
  selector: 'app-common-codes',
  imports: [TranslatePipe, Table, Popup, RouterModule],
  templateUrl: './common-codes.html',
  styleUrl: './common-codes.css',
})
export class CommonCodes implements OnInit {
  showPopup = false;
  faPencil = faPencil;
  faEye = faEye;
  faTrash = faTrash;
  popupTitle = '';
  isDeleteConfirm = false;
  isViewMode = false;
  deleteMessage = '';

  constructor(
    private translate: TranslateService,
    private router: Router,
    private setupService: Setup,
    private toast: ToastService
  ) { }

  ngOnInit(): void {
    this.loadCodeGroups();
  }

  popupFields: DynamicFieldConfig[] = [
    {
      label: 'COMMON.NAME',
      key: 'name',
      type: 'text',
      value: ''
    },
    {
      label: 'COMMON.STATUS',
      key: 'status',
      type: 'text',
      value: ''
    },
    {
      label: 'COMMON.DESCRIPTION',
      key: 'desc',
      type: 'text',
      value: ''
    }
  ];

  // ================= TABLE CONFIG =================
  columns: TableColumn[] = [
    { key: 'grpSeq', label: 'COMMON.CODE', type: 'text' },
    {
      key: 'grpName',
      label: 'COMMON.NAME',
      type: 'link',
      onClick: (row) => this.router.navigate(['/common-code-values'], { queryParams: { grpSeq: row.grpSeq } })
    },
    { key: 'grpDscr', label: 'COMMON.DESCRIPTION', type: 'text' },
    {
      key: 'grpActvFlg',
      label: 'COMMON.STATUS',
      type: 'badge',
      map: { true: 'COMMON.ACTIVE', false: 'COMMON.INACTIVE' },
      badgeMap: {
        true: 'status-active',
        false: 'status-inactive'
      }
    }
  ];

  codeGroups: CommonCodeGroup[] = [];
  loading = false;

  loadCodeGroups() {
    this.loading = true;
    this.setupService.getCommonCodeGroups().subscribe({
      next: (res: any) => {
        this.loading = false;
        // API likely returns array. Map if necessary.
        this.codeGroups = Array.isArray(res) ? res : res.data || [];
      },
      error: (err) => {
        this.loading = false;
        console.error('Failed to load code groups', err);
      }
    });
  }

  selectedRow: CommonCodeGroup | null = null;

  openRefCd() {
    this.selectedRow = null;
    this.popupTitle = 'SETUP.COMMON_CODES.ADD_GROUP';
    this.isDeleteConfirm = false;
    this.isViewMode = false;
    this.popupFields = [
      {
        label: 'SETUP.COMMON_CODES.GROUP_NAME',
        key: 'grpName',
        type: 'text',
        value: '',
        validations: { required: true }
      },
      {
        label: 'COMMON.DESCRIPTION',
        key: 'grpDscr',
        type: 'text',
        value: ''
      },
      {
        label: 'COMMON.STATUS',
        key: 'grpActvFlg',
        type: 'select',
        value: true,
        options$: [{ label: 'COMMON.ACTIVE', value: true }, { label: 'COMMON.INACTIVE', value: false }]
      }
    ];
    this.showPopup = true;
  }

  // ================= TABLE ACTIONS =================
  actions: TableAction[] = [
    {
      icon: this.faPencil,
      class: 'text-indigo-600',
      tooltip: 'COMMON.EDIT',
      onClick: (row: any) => {
        this.selectedRow = row;
        this.popupTitle = 'SETUP.COMMON_CODES.EDIT_GROUP';
        this.isDeleteConfirm = false;
        this.isViewMode = false;
        this.popupFields = [
          {
            label: 'SETUP.COMMON_CODES.GROUP_NAME',
            key: 'grpName',
            type: 'text',
            value: row.grpName,
            validations: { required: true }
          },
          {
            label: 'COMMON.DESCRIPTION',
            key: 'grpDscr',
            type: 'text',
            value: row.grpDscr
          },
          {
            label: 'COMMON.STATUS',
            key: 'grpActvFlg',
            type: 'select',
            value: row.grpActvFlg, // true/false
            options$: [{ label: 'COMMON.ACTIVE', value: true }, { label: 'COMMON.INACTIVE', value: false }]
          }
        ];
        this.showPopup = true;
      }
    },
    {
      icon: this.faEye,
      class: 'text-green-600',
      tooltip: 'COMMON.VIEW',
      onClick: (row: any) => {
        this.selectedRow = row;
        this.popupTitle = 'SETUP.COMMON_CODES.VIEW_GROUP';
        this.isDeleteConfirm = false;
        this.isViewMode = true;
        this.popupFields = [
          { label: 'SETUP.COMMON_CODES.GROUP_NAME', key: 'grpName', type: 'text', value: row.grpName },
          { label: 'COMMON.DESCRIPTION', key: 'grpDscr', type: 'text', value: row.grpDscr },
          {
            label: 'COMMON.STATUS',
            key: 'grpActvFlg',
            type: 'select',
            value: row.grpActvFlg,
            options$: [{ label: 'COMMON.ACTIVE', value: true }, { label: 'COMMON.INACTIVE', value: false }]
          }
        ];
        this.showPopup = true;
      }
    },
    {
      icon: this.faTrash,
      class: 'text-red-600',
      tooltip: 'COMMON.DELETE',
      onClick: (row: any) => {
        this.selectedRow = row;
        this.popupTitle = 'SETUP.COMMON_CODES.DELETE_GROUP';
        this.isDeleteConfirm = true;
        this.isViewMode = false;
        this.deleteMessage = this.translate.instant('SETUP.CONFIRM_DELETE_NAME', { name: row.grpName });
        this.showPopup = true;
      }
    }
  ];


  SaveCommonCode(formData: any): void {
    const payload = this.createPayload(formData);
    console.log('Validating Payload:', payload);

    this.setupService.saveCommonCodeGroup(payload).subscribe({
      next: (res) => {
        this.toast.success(this.isDeleteConfirm ? 'SETUP.DELETED_SUCCESSFULLY' : 'SETUP.SAVED_SUCCESSFULLY');
        this.showPopup = false;
        this.loadCodeGroups();
      },
      error: (err) => {
        console.error('Save failed', err);
        this.toast.error('SETUP.FAILED_TO_SAVE_RECORD');
      }
    });
  }

  onDeleteConfirmed(): void {
    const payload = this.createPayload({});

    this.setupService.saveCommonCodeGroup(payload).subscribe({
      next: () => {
        this.toast.success('SETUP.DELETED_SUCCESSFULLY');
        this.showPopup = false;
        this.loadCodeGroups();
      },
      error: (err) => {
        console.error('Delete failed', err);
        this.toast.error('SETUP.FAILED_TO_DELETE_RECORD');
      }
    });
  }

  private createPayload(formData: any): CommonCodeGroup {
    const isDelete = this.isDeleteConfirm;

    // Check if we have a selected row for Edit/Delete actions
    // If New, selectedRow is null.

    return {
      tntKey: 0,
      grpSeq: this.selectedRow?.grpSeq ?? null,
      grpId: this.selectedRow?.grpId ?? "0",

      // If Delete, use existing values. If Edit/Create, use Form values.
      grpName: (isDelete && this.selectedRow) ? this.selectedRow.grpName : formData.grpName,
      grpDscr: (isDelete && this.selectedRow) ? (this.selectedRow.grpDscr ?? null) : (formData.grpDscr ?? null),
      grpActvFlg: (isDelete && this.selectedRow) ? this.selectedRow.grpActvFlg : (formData.grpActvFlg ?? true),

      delFlg: isDelete ? 1 : 0
    };
  }
}
